import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Card,
    CardContent,
    CardActionArea,
    Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../../services/api';
import logger from '../../utils/logger';
import { useDefaultProfile } from '../../contexts/DefaultProfileContext';
import { getSession } from '../../utils/authUtils';
import { useProfilePlan } from '../../contexts/ProfilePlanContext';
import GMBService from '../../services/GMBService';
import tokenManager from '../../auth/TokenManager';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        padding: '8px'
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '24px',
    fontWeight: 600,
    color: '#121927',
    paddingBottom: '8px'
}));

const ProfileCard = styled(Card)(({ theme, hasplan }) => ({
    borderRadius: '12px',
    border: hasplan === 'true' ? '2px solid #34A853' : '2px solid #E0E0E0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        transform: 'translateY(-2px)'
    }
}));

const PlanBadge = styled(Chip)(({ theme, plantype }) => {
    const colors = {
        basic: { bg: '#E3F2FD', text: '#1976D2' },
        advance: { bg: '#F3E5F5', text: '#7B1FA2' },
        pro: { bg: '#FFF3E0', text: '#E65100' },
        none: { bg: '#FFEBEE', text: '#C62828' }
    };
    const colorSet = colors[plantype] || colors.none;

    return {
        backgroundColor: colorSet.bg,
        color: colorSet.text,
        fontWeight: 600,
        fontSize: '12px',
        height: '24px',
        textTransform: 'capitalize'
    };
});

const ProfileSelectorModal = ({ open, onClose, targetRoute }) => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const { setDefaultProfile } = useDefaultProfile();
    const { loadProfileSubscription } = useProfilePlan();
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            fetchProfiles();
        }
    }, [open]);

    const fetchProfiles = async () => {
        setLoading(true);
        setError(null);

        try {
            logger.log('[ProfileSelector] Fetching user GMB profiles...');

            // Get user session (uses getSession from authUtils)
            const session = getSession();

            if (!session) {
                throw new Error('No active session found. Please login again.');
            }

            // Get user ID from session - try multiple possible fields
            const userId = session?.id || session?.user?.id || session?.user_id;

            if (!userId) {
                logger.error('[ProfileSelector] Session found but no user ID:', session);
                throw new Error('User ID not found in session. Please try logging out and back in.');
            }

            logger.log('[ProfileSelector] User ID:', userId);

            // Determine current user identity for cache scoping
            let currentOwner = (session?.email || session?.user?.email || '').toString().toLowerCase() || 'anonymous';

            // Check for admin access mode
            try {
                const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
                if (adminAccessDataRaw) {
                    const adminAccessData = JSON.parse(adminAccessDataRaw);
                    if (adminAccessData?.targetUserId) {
                        currentOwner = `admin-view:${adminAccessData.targetUserId}`;
                    }
                }
            } catch (_) { }

            // Try to get profiles from localStorage (same as Performance page)
            const cachedProfiles = localStorage.getItem('availableGMBProfiles');
            const cachedOwner = localStorage.getItem('availableGMBProfilesOwner');
            const cacheOwnerMatches = cachedOwner && String(cachedOwner) === String(currentOwner);

            let profilesData = [];

            if (cachedProfiles && cacheOwnerMatches) {
                try {
                    const profiles = JSON.parse(cachedProfiles);
                    if (Array.isArray(profiles) && profiles.length > 0) {
                        logger.log('[ProfileSelector] Found cached GMB profiles:', profiles.length);

                        // Transform to modal format with plan information
                        profilesData = profiles.map(profile => ({
                            id: profile.id || profile.locationId,
                            locationId: profile.locationId || profile.id,
                            accountId: profile.accountId,
                            name: profile.name || profile.title || 'Unnamed Business',
                            address: profile.address || 'No address',
                            plan: profile.plan || null, // basic, advance, pro, or null
                            locationData: profile.locationData || profile
                        }));
                    }
                } catch (e) {
                    logger.warn('[ProfileSelector] Error parsing cached profiles:', e);
                }
            }

            // If no cached data, fetch from Google GMB API directly (like Dashboard does)
            if (profilesData.length === 0) {
                logger.log('[ProfileSelector] No cached profiles, fetching from Google GMB API...');

                try {
                    // Get existing Google access token
                    let accessToken = localStorage.getItem('googleAccessToken');

                    if (!accessToken) {
                        const tokenData = tokenManager.get('google');
                        accessToken = tokenData?.access_token;
                    }

                    if (!accessToken) {
                        throw new Error('No Google access token found. Please visit Dashboard to connect your Google My Business account.');
                    }

                    logger.log('[ProfileSelector] Using Google access token to fetch GMB profiles');

                    // Fetch GMB accounts from Google (same as Dashboard)
                    const accounts = await GMBService.getAccounts(accessToken);

                    if (accounts.length === 0) {
                        throw new Error('No GMB accounts found. Please ensure your Google account has access to Google My Business profiles.');
                    }

                    // Get all locations for all accounts
                    const allLocations = [];
                    for (const acct of accounts) {
                        try {
                            if (!acct?.name) continue;
                            const locs = await GMBService.getLocations(accessToken, acct.name);
                            if (Array.isArray(locs) && locs.length) {
                                locs.forEach(l => allLocations.push({ account: acct, loc: l }));
                            }
                        } catch (e) {
                            logger.warn('[ProfileSelector] Failed to fetch locations for account', acct?.name, e?.message || e);
                        }
                    }

                    if (allLocations.length === 0) {
                        throw new Error('No business locations found. Please ensure your Google My Business account has verified business locations.');
                    }

                    // Transform to profile format
                    profilesData = allLocations.map(({ account, loc }) => ({
                        id: loc.name?.split('/').pop(),
                        locationId: loc.name?.split('/').pop(),
                        accountId: account?.name?.split('/').pop(),
                        name: loc.title || loc.locationName || 'Unnamed Location',
                        address: loc.storefrontAddress?.formattedAddress ||
                            loc.storefrontAddress?.addressLines?.join(', ') ||
                            'No address',
                        plan: null, // Plan needs to be fetched from database
                        locationData: loc,
                        accountData: account
                    }));

                    // Cache for future use
                    localStorage.setItem('availableGMBProfiles', JSON.stringify(profilesData));
                    localStorage.setItem('availableGMBProfilesOwner', String(currentOwner));

                    logger.log('[ProfileSelector] Fetched and cached', profilesData.length, 'profiles from Google');

                } catch (gmbError) {
                    logger.error('[ProfileSelector] Error fetching from Google GMB API:', gmbError);
                    throw new Error(gmbError.message || 'Failed to load GMB profiles from Google. Please visit Dashboard first.');
                }
            }

            // Fetch plan data using ProfilePlanContext (same as BusinessTable)
            if (profilesData.length > 0) {
                try {
                    logger.log('[ProfileSelector] Fetching plan data for', profilesData.length, 'profiles using ProfilePlanContext');

                    const planResults = {};

                    // Fetch plans sequentially to avoid race conditions
                    for (const profile of profilesData) {
                        try {
                            const locId = profile.locationId || profile.id;

                            // Backend looks up by location_id column in GmbAccount table,
                            // so send locationId directly (not accountId:locationId format)
                            const profileKey = String(locId);

                            logger.log('[ProfileSelector] Loading plan for locationId:', locId);

                            const resp = await loadProfileSubscription(profileKey);
                            logger.log('[ProfileSelector] Raw response for', locId, ':', resp);

                            const plan = resp?.planCode || resp?.plan || (resp?.data && resp.data.plan) || null;

                            planResults[locId] = plan;
                            logger.log('[ProfileSelector] ✅ Plan for', locId, ':', plan);
                        } catch (e) {
                            logger.error('[ProfileSelector] ❌ Error loading plan for profile:', profile.locationId, e);
                            planResults[profile.locationId] = null;
                        }
                    }

                    logger.log('[ProfileSelector] All fetched plans:', planResults);

                    // Merge plan data into profiles
                    profilesData = profilesData.map(profile => {
                        const mergedPlan = planResults[profile.locationId] || null;
                        logger.log('[ProfileSelector] Merging profile:', profile.locationId, 'with plan:', mergedPlan);

                        return {
                            ...profile,
                            plan: mergedPlan
                        };
                    });

                    const profilesWithPlans = profilesData.filter(p => p.plan);
                    logger.log('[ProfileSelector] ✅ Final result:', profilesWithPlans.length, 'of', profilesData.length, 'profiles have plans');
                    profilesData.forEach(p => {
                        logger.log('[ProfileSelector] Profile:', p.name, '| LocationID:', p.locationId, '| Plan:', p.plan);
                    });
                } catch (planError) {
                    logger.error('[ProfileSelector] Error fetching plan data:', planError);
                    logger.warn('[ProfileSelector] Continuing without plan data - all profiles will show as "No Plan"');
                    // Continue without plan data - profiles will all show as null plan
                }
            }

            logger.log('[ProfileSelector] Final processed profiles:', profilesData);
            setProfiles(profilesData);

        } catch (err) {
            logger.error('[ProfileSelector] Error fetching profiles:', err);
            setError(err.message || 'Failed to load GMB profiles. Please visit the Dashboard page first to load your profiles.');
        } finally {
            setLoading(false);
        }
    };


    const handleProfileSelect = (profile) => {
        logger.log('[ProfileSelector] Profile selected:', profile);

        // Check if profile has a plan
        if (!profile.plan) {
            logger.log('[ProfileSelector] Profile has NO plan - showing upgrade message');
            setSelectedProfile(profile);
            return;
        }

        // Profile has a plan - proceed with navigation
        logger.log('[ProfileSelector] Profile has plan:', profile.plan);

        // Set as default profile
        setDefaultProfile({
            id: profile.id,
            locationId: profile.locationId,
            accountId: profile.accountId,
            name: profile.name,
            plan: profile.plan,
            locationData: profile.locationData
        });

        // Close modal
        onClose();

        // Navigate to target route
        if (targetRoute) {
            logger.log('[ProfileSelector] Navigating to:', targetRoute);
            navigate(targetRoute);
        }
    };

    const handleUpgradeClick = () => {
        logger.log('[ProfileSelector] Upgrade button clicked for profile:', selectedProfile);
        // You can implement upgrade flow here
        // For now, just close the modal and maybe navigate to pricing
        onClose();
        navigate('/pricing'); // or wherever your upgrade/pricing page is
    };

    const handleBackToSelection = () => {
        setSelectedProfile(null);
    };

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <StyledDialogTitle>
                {selectedProfile && !selectedProfile.plan ? 'Upgrade Required' : 'Select GMB Profile'}
            </StyledDialogTitle>

            <DialogContent>
                {loading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                        <CircularProgress size={40} sx={{ color: '#0B91D6', mb: 2 }} />
                        <Typography variant="body2" color="textSecondary">
                            Loading your GMB profiles...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : selectedProfile && !selectedProfile.plan ? (
                    // Show upgrade message for profile without plan
                    <Box py={2}>
                        <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: '#EF232A' }} />
                        </Box>

                        <Typography variant="h6" align="center" gutterBottom sx={{ fontWeight: 600 }}>
                            Upgrade to Pro Required
                        </Typography>

                        <Typography variant="body1" align="center" color="textSecondary" paragraph>
                            The profile "<strong>{selectedProfile.name}</strong>" doesn't have an active plan.
                        </Typography>

                        <Typography variant="body2" align="center" color="textSecondary" paragraph>
                            To access this feature, you need to upgrade this GMB profile to a Basic, Advance, or Pro plan.
                        </Typography>

                        <Box display="flex" gap={2} justifyContent="center" mt={4}>
                            <Button
                                variant="outlined"
                                onClick={handleBackToSelection}
                                sx={{
                                    borderColor: '#E0E0E0',
                                    color: '#121927',
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Back to Profiles
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleUpgradeClick}
                                sx={{
                                    backgroundColor: '#0B91D6',
                                    color: '#fff',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: '#0277BD'
                                    }
                                }}
                            >
                                Upgrade to Pro
                            </Button>
                        </Box>
                    </Box>
                ) : profiles.length === 0 ? (
                    <Box py={4} textAlign="center">
                        <BusinessIcon sx={{ fontSize: 64, color: '#BDBDBD', mb: 2 }} />
                        <Typography variant="body1" color="textSecondary">
                            No GMB profiles found. Please connect your Google My Business account first.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                onClose();
                                navigate('/dashboard');
                            }}
                            sx={{
                                mt: 3,
                                backgroundColor: '#0B91D6',
                                color: '#fff',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: '#0277BD' }
                            }}
                        >
                            Go to Dashboard
                        </Button>
                    </Box>
                ) : (
                    // Show profile selection grid
                    <>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Select a GMB profile to continue. Only profiles with an active plan can access this feature.
                        </Typography>

                        <Grid container spacing={2}>
                            {profiles.map((profile) => (
                                <Grid item xs={12} sm={6} key={profile.id}>
                                    <ProfileCard hasplan={profile.plan ? 'true' : 'false'}>
                                        <CardActionArea onClick={() => handleProfileSelect(profile)}>
                                            <CardContent>
                                                {/* Profile Header with Plan Badge */}
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <BusinessIcon sx={{ color: profile.plan ? '#34A853' : '#BDBDBD' }} />
                                                        {profile.plan && (
                                                            <CheckCircleIcon sx={{ fontSize: 20, color: '#34A853' }} />
                                                        )}
                                                    </Box>
                                                    <PlanBadge
                                                        label={profile.plan || 'No Plan'}
                                                        plantype={profile.plan || 'none'}
                                                        size="small"
                                                    />
                                                </Box>

                                                {/* Business Name */}
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '16px',
                                                        color: '#121927',
                                                        mb: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {profile.name}
                                                </Typography>

                                                {/* Address */}
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{
                                                        fontSize: '13px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {profile.address}
                                                </Typography>

                                                {!profile.plan && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            mt: 2,
                                                            color: '#EF232A',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        ⚠️ Upgrade required to use this profile
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </CardActionArea>
                                    </ProfileCard>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </DialogContent>
        </StyledDialog>
    );
};

export default ProfileSelectorModal;
